import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Stack,
  Text,
  Button,
  Spinner,
  HStack,
  Badge,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  Textarea,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TopMenu from "./TopMenu";
import PageGuide from "./components/PageGuide";
import {
  fetchLeaderInbox,
  answerAskExpertQuestion,
  approvePodcastEpisode,
} from "./services/leaderInboxService";
import { markRead } from "./services/notificationsService";
import { setReverseSeminarDecision } from "./services/reverseSeminarsService";

const STREAM_LABELS = {
  ask_expert_question: "Questions",
  podcast_episode: "Podcasts",
  event: "Events",
  reverse_seminar_case: "Seminars",
  winery: "Wineries",
};

export default function Inbox({ onLogout }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [leaderScopes, setLeaderScopes] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streamFilter, setStreamFilter] = useState(null);
  const [teamKeyFilter, setTeamKeyFilter] = useState(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [drafts, setDrafts] = useState({}); // {entity_id: text}
  const [submitting, setSubmitting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLeaderInbox({
        entityType: streamFilter || undefined,
        teamKey: teamKeyFilter || undefined,
        unreadOnly,
      });
      setItems(data.items || []);
      setLeaderScopes(data.leader_scopes || []);
      setCounts(data.counts_by_stream || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [streamFilter, teamKeyFilter, unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const onRowClick = async (item) => {
    if (!item.is_read) {
      try {
        await markRead(item.notification_id);
        setItems((prev) =>
          prev.map((i) =>
            i.notification_id === item.notification_id ? { ...i, is_read: true } : i,
          ),
        );
      } catch {
        /* ignore */
      }
    }
  };

  const onSubmitAnswer = async (item) => {
    const text = (drafts[item.entity_id] || "").trim();
    if (!text) return;
    setSubmitting((s) => ({ ...s, [item.entity_id]: true }));
    try {
      await answerAskExpertQuestion(item.entity_id, { answer: text });
      toast({ title: "Answer sent", status: "success", duration: 2000 });
      setDrafts((d) => ({ ...d, [item.entity_id]: "" }));
      load();
    } catch (err) {
      toast({ title: err.message, status: "error", duration: 3000 });
    } finally {
      setSubmitting((s) => ({ ...s, [item.entity_id]: false }));
    }
  };

  const onSeminarDecision = async (item, decision) => {
    const key = `rs_${item.entity_id}`;
    setSubmitting((s) => ({ ...s, [key]: true }));
    try {
      await setReverseSeminarDecision(item.entity_id, { decision });
      const label =
        decision === "accepted"
          ? "Case accepted"
          : decision === "revise"
            ? "Sent back for revisions"
            : decision === "rejected"
              ? "Case rejected"
              : "Decision recorded";
      toast({ title: label, status: "success", duration: 2000 });
      load();
    } catch (err) {
      toast({ title: err.message, status: "error", duration: 3000 });
    } finally {
      setSubmitting((s) => ({ ...s, [key]: false }));
    }
  };

  const onPodcastAction = async (item, action, recommended = false) => {
    setSubmitting((s) => ({ ...s, [`pod_${item.entity_id}`]: true }));
    try {
      await approvePodcastEpisode(item.entity_id, { action, recommended });
      toast({
        title: action === "approve" ? "Episode approved" : "Episode rejected",
        status: "success",
        duration: 2000,
      });
      load();
    } catch (err) {
      toast({ title: err.message, status: "error", duration: 3000 });
    } finally {
      setSubmitting((s) => ({ ...s, [`pod_${item.entity_id}`]: false }));
    }
  };

  const teamKeys = Array.from(new Set(leaderScopes.map((s) => s.team_key)));

  return (
    <Box bg="surface.canvas" minH="100vh">
      <TopMenu onLogout={onLogout} />
      <Box maxW="1100px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <Stack spacing={6}>
          <HStack justify="space-between" align="center">
            <HStack spacing={2} align="center">
              <Heading size="lg">Leader Inbox</Heading>
              <PageGuide id="inbox" />
            </HStack>
            <Button size="sm" variant="outline" onClick={load} isLoading={loading}>
              Refresh
            </Button>
          </HStack>

          {leaderScopes.length === 0 && !loading && (
            <Box p={6} borderWidth="1px" borderRadius="md" borderColor="border.default">
              <Text color="text.muted">
                You're not currently a leader for any theme or region. When a
                Thematic Leader or Regional Coordinator role is assigned to you,
                triage items will appear here.
              </Text>
            </Box>
          )}

          {leaderScopes.length > 0 && (
            <>
              {/* Stream filter chips */}
              <Wrap spacing={2}>
                <WrapItem>
                  <Button
                    size="sm"
                    variant={streamFilter === null ? "solid" : "outline"}
                    onClick={() => setStreamFilter(null)}
                  >
                    All
                  </Button>
                </WrapItem>
                {Object.entries(STREAM_LABELS).map(([key, label]) => (
                  <WrapItem key={key}>
                    <Button
                      size="sm"
                      variant={streamFilter === key ? "solid" : "outline"}
                      onClick={() => setStreamFilter(key)}
                    >
                      {label}
                      {counts[key] ? (
                        <Badge ml={2} colorScheme="red" borderRadius="full">
                          {counts[key]}
                        </Badge>
                      ) : null}
                    </Button>
                  </WrapItem>
                ))}
                <WrapItem>
                  <Button
                    size="sm"
                    variant={unreadOnly ? "solid" : "outline"}
                    colorScheme={unreadOnly ? "blue" : "gray"}
                    onClick={() => setUnreadOnly((v) => !v)}
                  >
                    Unread only
                  </Button>
                </WrapItem>
              </Wrap>

              {/* Team-key filter */}
              {teamKeys.length > 1 && (
                <Wrap spacing={2}>
                  <WrapItem>
                    <Tag
                      size="md"
                      variant={teamKeyFilter === null ? "solid" : "subtle"}
                      cursor="pointer"
                      onClick={() => setTeamKeyFilter(null)}
                    >
                      <TagLabel>All areas</TagLabel>
                    </Tag>
                  </WrapItem>
                  {teamKeys.map((tk) => (
                    <WrapItem key={tk}>
                      <Tag
                        size="md"
                        variant={teamKeyFilter === tk ? "solid" : "subtle"}
                        cursor="pointer"
                        onClick={() => setTeamKeyFilter(tk)}
                      >
                        <TagLabel>{tk}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </>
          )}

          {loading && <Spinner />}
          {error && <Text color="red.500">{error}</Text>}
          {!loading && !error && items.length === 0 && leaderScopes.length > 0 && (
            <Text color="text.muted">Nothing to triage right now. Inbox zero. 🎉</Text>
          )}

          {!loading && items.length > 0 && (
            <Stack spacing={3}>
              {items.map((item) => {
                const isAsk = item.entity_type === "ask_expert_question";
                const isPod = item.entity_type === "podcast_episode";
                const isSeminar = item.entity_type === "reverse_seminar_case";
                const e = item.entity || {};
                return (
                  <Box
                    key={item.notification_id}
                    borderWidth="1px"
                    borderRadius="md"
                    borderColor="border.default"
                    bg={item.is_read ? "surface.card" : "blue.50"}
                    p={4}
                    onClick={() => onRowClick(item)}
                  >
                    <HStack justify="space-between" align="start" mb={2}>
                      <HStack spacing={2}>
                        <Badge colorScheme="purple">
                          {STREAM_LABELS[item.entity_type] || item.entity_type}
                        </Badge>
                        <Badge variant="subtle">
                          {item.scope}/{item.team_key}
                        </Badge>
                        {isAsk && e.status && (
                          <Badge
                            colorScheme={
                              e.status === "answered"
                                ? "green"
                                : e.status === "waiting"
                                  ? "yellow"
                                  : "gray"
                            }
                          >
                            {e.status}
                          </Badge>
                        )}
                        {isPod && e.approval_status && (
                          <Badge
                            colorScheme={
                              e.approval_status === "approved"
                                ? "green"
                                : e.approval_status === "rejected"
                                  ? "red"
                                  : "orange"
                            }
                          >
                            {e.approval_status}
                          </Badge>
                        )}
                        {isPod && e.recommended_badge && (
                          <Badge colorScheme="yellow">Recommended</Badge>
                        )}
                        {isSeminar && e.stage && (
                          <Badge
                            colorScheme={
                              e.stage === "decision_pending"
                                ? "orange"
                                : e.stage === "pilot_active"
                                  ? "blue"
                                  : e.stage === "pilot_completed"
                                    ? "green"
                                    : e.stage === "archived"
                                      ? "gray"
                                      : "purple"
                            }
                          >
                            {e.stage.replace(/_/g, " ")}
                          </Badge>
                        )}
                        {isSeminar && e.decision && e.decision !== "pending" && (
                          <Badge
                            colorScheme={
                              e.decision === "accepted"
                                ? "green"
                                : e.decision === "rejected"
                                  ? "red"
                                  : "yellow"
                            }
                          >
                            {e.decision}
                          </Badge>
                        )}
                        {isSeminar && e.urgency && (
                          <Badge
                            colorScheme={
                              e.urgency === "critical"
                                ? "red"
                                : e.urgency === "high"
                                  ? "orange"
                                  : e.urgency === "low"
                                    ? "gray"
                                    : "yellow"
                            }
                            variant="subtle"
                          >
                            {e.urgency}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="text.muted">
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString()
                          : ""}
                      </Text>
                    </HStack>

                    {isAsk && e.title && (
                      <>
                        <Text fontWeight="semibold">{e.title}</Text>
                        {e.snippet && (
                          <Text fontSize="sm" color="text.muted" mt={1}>
                            {e.snippet}
                          </Text>
                        )}
                        {e.requester_name && (
                          <Text fontSize="xs" color="text.muted" mt={1}>
                            From: {e.requester_name}
                          </Text>
                        )}

                        <Divider my={3} />

                        {e.has_answer ? (
                          <Text fontSize="sm" color="gwbi.600">
                            Already answered.
                          </Text>
                        ) : (
                          <Stack spacing={2} onClick={(ev) => ev.stopPropagation()}>
                            <Textarea
                              size="sm"
                              placeholder="Write a reply…"
                              value={drafts[item.entity_id] || ""}
                              onChange={(ev) =>
                                setDrafts((d) => ({
                                  ...d,
                                  [item.entity_id]: ev.target.value,
                                }))
                              }
                            />
                            <HStack>
                              <Button
                                size="sm"
                                colorScheme="gwbi"
                                onClick={() => onSubmitAnswer(item)}
                                isLoading={!!submitting[item.entity_id]}
                                isDisabled={!(drafts[item.entity_id] || "").trim()}
                              >
                                Send answer
                              </Button>
                            </HStack>
                          </Stack>
                        )}
                      </>
                    )}

                    {isPod && e.title && (
                      <>
                        <Text fontWeight="semibold">{e.title}</Text>
                        {e.voice && (
                          <Text fontSize="xs" color="text.muted" mt={1}>
                            Voice: {e.voice}
                          </Text>
                        )}
                        {e.audio_url && (
                          <Box mt={2}>
                            <audio controls src={e.audio_url} style={{ width: "100%", height: 32 }} />
                          </Box>
                        )}

                        <Divider my={3} />

                        {e.approval_status === "approved" ? (
                          <Text fontSize="sm" color="gwbi.600">
                            Approved{e.recommended_badge ? " and recommended" : ""}.
                          </Text>
                        ) : e.approval_status === "rejected" ? (
                          <Text fontSize="sm" color="red.500">Rejected.</Text>
                        ) : (
                          <HStack spacing={2} onClick={(ev) => ev.stopPropagation()}>
                            <Button
                              size="sm"
                              colorScheme="gwbi"
                              onClick={() => onPodcastAction(item, "approve")}
                              isLoading={!!submitting[`pod_${item.entity_id}`]}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="gwbi"
                              variant="outline"
                              onClick={() => onPodcastAction(item, "approve", true)}
                              isLoading={!!submitting[`pod_${item.entity_id}`]}
                            >
                              Approve + Recommend
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => onPodcastAction(item, "reject")}
                              isLoading={!!submitting[`pod_${item.entity_id}`]}
                            >
                              Reject
                            </Button>
                          </HStack>
                        )}
                      </>
                    )}

                    {isSeminar && (
                      <>
                        <Text fontWeight="semibold">
                          {e.title || "Untitled case"}
                        </Text>
                        {e.snippet && (
                          <Text fontSize="sm" color="text.muted" mt={1}>
                            {e.snippet}
                          </Text>
                        )}
                        {e.author_name && (
                          <Text fontSize="xs" color="text.muted" mt={1}>
                            From: {e.author_name}
                          </Text>
                        )}

                        <Divider my={3} />

                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              navigate(
                                `/tool/reverse-seminars#case-${item.entity_id}`,
                              )
                            }
                          >
                            Open case
                          </Button>
                          {e.stage === "decision_pending" &&
                            (e.decision === "pending" || !e.decision) && (
                              <>
                                <Button
                                  size="sm"
                                  colorScheme="gwbi"
                                  isLoading={
                                    !!submitting[`rs_${item.entity_id}`]
                                  }
                                  onClick={() =>
                                    onSeminarDecision(item, "accepted")
                                  }
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="yellow"
                                  variant="outline"
                                  isLoading={
                                    !!submitting[`rs_${item.entity_id}`]
                                  }
                                  onClick={() =>
                                    onSeminarDecision(item, "revise")
                                  }
                                >
                                  Request revisions
                                </Button>
                                <Button
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  isLoading={
                                    !!submitting[`rs_${item.entity_id}`]
                                  }
                                  onClick={() =>
                                    onSeminarDecision(item, "rejected")
                                  }
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                        </HStack>
                      </>
                    )}

                    {!isAsk && !isPod && !isSeminar && (
                      <Text fontSize="sm">{item.message}</Text>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
